/**
 * The shared visitor cookie behind the marketing → app funnel.
 *
 * The cookie is written on the **parent** domain (`.thesukha.co`) so that
 * www.thesukha.co and app.thesukha.co see the same value. Today only the app
 * writes it, which means it records the last hop into the app rather than the
 * true origin of the visit. When the marketing site starts writing the same
 * cookie on first arrival, the app side needs no change: it already reads
 * whatever is there in preference to minting its own.
 */

export const LANDING_COOKIE_NAME = "sukha_landing";

/** A year — long enough to survive a trial and the decision that follows it. */
const LANDING_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

const PARENT_DOMAIN = "thesukha.co";

export interface LandingCookieValue {
  /** Anonymous visitor id, stitched to the member's id once they credential. */
  visitorId: string;
  referrerUrl: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  landingPath: string | null;
  /** ISO timestamp of first arrival. */
  landedAt: string;
}

function isLandingCookieValue(value: unknown): value is LandingCookieValue {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<LandingCookieValue>;

  return (
    typeof candidate.visitorId === "string" &&
    typeof candidate.landedAt === "string"
  );
}

/**
 * The widest domain we are allowed to set a cookie on.
 *
 * On `app.thesukha.co` that is `.thesukha.co`, so the marketing site can read
 * it. Anywhere else — localhost, preview deployments, custom hosts — the
 * browser rejects a cross-site domain, so the cookie stays host-only.
 */
function landingCookieDomain(): string | null {
  const { hostname } = window.location;

  return hostname === PARENT_DOMAIN || hostname.endsWith(`.${PARENT_DOMAIN}`)
    ? `.${PARENT_DOMAIN}`
    : null;
}

/**
 * A visitor id, without assuming a secure context.
 *
 * `crypto.randomUUID` only exists on https and localhost, so on a plain-http
 * preview host it is undefined and would throw. The fallback is not
 * cryptographically strong and does not need to be — this identifies an
 * anonymous visitor for attribution, it is not a secret or a session token.
 */
function newVisitorId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function readLandingCookie(): LandingCookieValue | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${LANDING_COOKIE_NAME}=`));

  if (!match) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(
      decodeURIComponent(match.slice(LANDING_COOKIE_NAME.length + 1))
    );
    return isLandingCookieValue(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Build the landing record for the page currently being viewed.
 *
 * Exported for the sake of tests; production code goes through
 * {@link ensureLandingCookie}, which only calls this on a first arrival.
 */
export function buildLandingCookieValue(
  visitorId: string,
  landedAt: string
): LandingCookieValue {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || null;

  return {
    visitorId,
    // Our own pages are not a referrer worth recording — the member was
    // already here.
    referrerUrl:
      referrer && !referrer.startsWith(window.location.origin)
        ? referrer
        : null,
    utmSource: params.get("utm_source"),
    utmMedium: params.get("utm_medium"),
    utmCampaign: params.get("utm_campaign"),
    utmContent: params.get("utm_content"),
    utmTerm: params.get("utm_term"),
    landingPath: window.location.pathname,
    landedAt,
  };
}

/**
 * Read the landing cookie, writing one for this arrival if there isn't one.
 *
 * @returns The landing record for this visitor, or null outside the browser
 */
export function ensureLandingCookie(): LandingCookieValue | null {
  if (typeof document === "undefined") {
    return null;
  }

  const existing = readLandingCookie();

  if (existing) {
    return existing;
  }

  const value = buildLandingCookieValue(newVisitorId(), new Date().toISOString());
  const domain = landingCookieDomain();

  try {
    document.cookie = [
      `${LANDING_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}`,
      "path=/",
      `max-age=${LANDING_COOKIE_MAX_AGE_SECONDS}`,
      "SameSite=Lax",
      ...(domain ? [`domain=${domain}`] : []),
      ...(window.location.protocol === "https:" ? ["Secure"] : []),
    ].join(";");
  } catch {
    // Cookies disabled. Attribution is lost for this visitor, which is the
    // correct outcome — it must not cost them the page.
  }

  return value;
}
